import {
  ApiCompetition,
  ApiCompetitionBulkAttemptData,
  ApiCompetitionEntryInline,
} from "griptonite-api-client";
import { createContext, useState, useContext, useMemo } from "react";
import { createStore, useStore } from "zustand";
import { combine, devtools } from "zustand/middleware";

import { Route } from "@/core/types";

type AttemptState = {
  success: boolean;
  segmentsCount?: number | null;
  tries: number;
  segmentTries?: number | null;
};

const createEntriesEditStore = () =>
  createStore(
    devtools(
      combine(
        {
          attemptState: {} as Record<string, AttemptState>,
          selectedCategoryUuid: null as string | null,
          tickedRoutesOnly: false,
          competitorSearchQuery: "",
        },
        (set) => ({
          setAttemptState: (
            userUuid: string,
            routeUuid: string,
            attemptStateUpdate: {
              success: boolean;
              segmentsCount?: number | null;
              tries: number;
              segmentTries?: number | null;
            },
          ) =>
            set((state) => ({
              attemptState: {
                ...state.attemptState,
                [`${userUuid}:${routeUuid}`]: {
                  ...state.attemptState[`${userUuid}:${routeUuid}`],
                  ...attemptStateUpdate,
                },
              },
            })),
          clearAttempts: () => set({ attemptState: {} }),
          setSelectedCategoryUuid: (selectedCategoryUuid: string | null) =>
            set({ selectedCategoryUuid }),
          setCompetitorSearchQuery: (query: string) => set({ competitorSearchQuery: query }),
        }),
      ),
    ),
  );

type State = ReturnType<ReturnType<typeof createEntriesEditStore>["getState"]>;

const EntriesEditContext = createContext<ReturnType<typeof createEntriesEditStore> | undefined>(
  undefined,
);

export const EntriesEditContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [store] = useState(createEntriesEditStore);
  return <EntriesEditContext.Provider value={store}>{children}</EntriesEditContext.Provider>;
};

export function useEntriesEditContext(): State;
export function useEntriesEditContext<T>(selector: (state: State) => T): T;
export function useEntriesEditContext<T>(selector?: (state: State) => T) {
  const store = useContext(EntriesEditContext);

  if (!store) {
    throw new Error("useEntriesEditContext must be used within a EntriesEditContextProvider");
  }

  return useStore(store, selector!);
}

export const useEntryAttemptEditState = (routeUuid: string, userUuid: string) => {
  const state = useEntriesEditContext((state) => state.attemptState[`${userUuid}:${routeUuid}`]);
  const setAttemptState = useEntriesEditContext((state) => state.setAttemptState);

  return [
    state,
    useMemo(() => {
      return (state: AttemptState) => {
        setAttemptState(userUuid, routeUuid, state);
      };
    }, [setAttemptState, userUuid, routeUuid]),
  ] as [AttemptState | undefined, (state: AttemptState) => void];
};

type ScoreCalcParams = {
  competition: ApiCompetition;
  entry: ApiCompetitionEntryInline;
  route: Route;
  attemptInfo: {
    success: boolean;
    count: number;
    segmentsCount?: number | null;
    segmentTriesCount?: number | null;
  };
};

const getScoringSystemAttemptsPoints = ({ competition, attemptInfo }: ScoreCalcParams) => {
  if (!attemptInfo.success) {
    if (attemptInfo.segmentsCount) {
      return competition.scoringSystemSettings.segment || 0;
    }
  } else {
    if (attemptInfo.count === 1) {
      return competition.scoringSystemSettings.flash || 0;
    } else if (attemptInfo.count === 2) {
      return competition.scoringSystemSettings.secondTry || 0;
    } else if (attemptInfo.count === 3) {
      return competition.scoringSystemSettings.thirdTry || 0;
    }
    return competition.scoringSystemSettings.fourPlusTries || 0;
  }
  return 0;
};

const getScoringSystemTicksOnlyPoints = ({ competition, attemptInfo }: ScoreCalcParams) => {
  if (attemptInfo.success) {
    let score = competition.scoringSystemSettings.tick || 0;
    if (attemptInfo.count === 1) {
      score += competition.scoringSystemSettings.flashBonus || 0;
    }
  }
  return 0;
};

const getScoringSystemDynamicPoints = ({ competition, route, attemptInfo }: ScoreCalcParams) => {
  // TODO: fixme
  if (attemptInfo.success) {
    return (competition.scoringSystemSettings.basePoints || 0) / (route.successesCount || 1);
  }
  return 0;
};

const getScoringSystemCircuitNumberPoints = ({
  competition,
  route,
  attemptInfo,
}: ScoreCalcParams) => {
  if (attemptInfo.success) {
    let score =
      (route.circuitNumber || 1) * (competition.scoringSystemSettings.pointsMultiplier || 1);
    if (attemptInfo.count === 1) {
      score += competition.scoringSystemSettings.flashBonus || 0;
    }
    return score;
  }
  return 0;
};

const calculateAttemptScore = (params: ScoreCalcParams) => {
  switch (params.competition.scoringSystem) {
    case "ATTEMPTS":
      return getScoringSystemAttemptsPoints(params);
    case "TICKS_ONLY":
      return getScoringSystemTicksOnlyPoints(params);
    case "DYNAMIC":
      return getScoringSystemDynamicPoints(params);
    case "CIRCUIT_NUMBER":
      return getScoringSystemCircuitNumberPoints(params);
    default:
      return undefined;
  }
};

export const calculateScoreChange = ({
  competition,
  entry,
  route,
  attemptInfo,
  state,
}: Omit<ScoreCalcParams, "attemptInfo"> & {
  attemptInfo: ApiCompetitionBulkAttemptData | undefined;
  state: AttemptState;
}) => {
  const prevScore = attemptInfo
    ? calculateAttemptScore({
        competition,
        entry,
        route,
        attemptInfo,
      })
    : undefined;
  const score = calculateAttemptScore({
    competition,
    entry,
    route,
    attemptInfo: {
      ...attemptInfo,
      success: state.success,
      count: state.tries,
      segmentsCount: state.segmentsCount ?? null,
      segmentTriesCount: state.segmentTries ?? null,
    },
  });
  if (score === undefined) {
    return undefined;
  }
  return score - (prevScore || 0);
};
